import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { portalApi, formatPortalError } from "@/lib/portalApi";
import Spade from "@/components/Spade";
import { PORTAL } from "@/constants/testIds";
import { ArrowRight, MailCheck } from "lucide-react";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [sent, setSent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            await portalApi.post("/portal/forgot-password", { email: email.trim() });
            setSent(true);
        } catch (err) {
            setError(formatPortalError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            data-testid={PORTAL.forgotRoot}
            className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden bg-[#2a0303]"
        >
            <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#4A0505] via-[#3B0404] to-[#0d0000]" />
            <div className="absolute right-[-6%] top-[-4%] opacity-[0.05]">
                <Spade size={520} />
            </div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="w-full max-w-md surface-glass rounded-3xl border border-[var(--border-subtle)] p-8 md:p-10"
            >
                <div className="flex items-center gap-2 mb-8">
                    <Spade size={24} />
                    <span className="font-display text-xl text-[var(--text-primary)]">
                        Aestherium<span className="text-[var(--accent-gold)]">.</span>
                    </span>
                </div>

                {sent ? (
                    <div data-testid={PORTAL.forgotSuccess}>
                        <div className="w-12 h-12 rounded-full bg-[rgba(212,175,55,0.15)] border border-[var(--border-accent)] flex items-center justify-center mb-5">
                            <MailCheck size={20} className="text-[var(--accent-gold)]" />
                        </div>
                        <h2 className="font-display text-3xl font-bold text-[var(--text-primary)] mb-3">
                            Check your inbox.
                        </h2>
                        <p className="text-[var(--text-secondary)] leading-relaxed mb-8">
                            If an account exists for that email, a reset link is on its way. The
                            link expires in one hour. If it does not arrive, message the Aestherium
                            team on WhatsApp and we will reset it for you.
                        </p>
                        <Link
                            to="/portal"
                            className="inline-flex items-center gap-2 text-sm text-[var(--accent-champagne)] hover:text-[var(--accent-gold)]"
                        >
                            ← Back to login
                        </Link>
                    </div>
                ) : (
                    <form onSubmit={submit}>
                        <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent-gold)] mb-3">
                            Password Reset
                        </p>
                        <h2 className="font-display text-4xl font-bold text-[var(--text-primary)] mb-3">
                            Forgot password?
                        </h2>
                        <p className="text-[var(--text-secondary)] mb-8">
                            Enter your account email and we will send you a secure reset link.
                        </p>

                        <label className="block text-xs uppercase tracking-[0.25em] text-[var(--accent-champagne)]/70 mb-2">
                            Email
                        </label>
                        <input
                            data-testid={PORTAL.forgotEmail}
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="w-full mb-6 bg-transparent border border-[var(--border-subtle)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-gold)]"
                        />

                        {error && (
                            <p className="mb-5 text-sm text-red-300 bg-red-900/30 border border-red-500/30 rounded-lg px-4 py-3">
                                {error}
                            </p>
                        )}

                        <button
                            data-testid={PORTAL.forgotSubmit}
                            type="submit"
                            disabled={loading}
                            className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-[var(--accent-gold)] text-[var(--brand-dark)] font-semibold uppercase tracking-[0.2em] text-sm hover:bg-[var(--accent-champagne)] transition-colors disabled:opacity-70"
                        >
                            {loading ? "Sending…" : "Send reset link"}
                            <ArrowRight size={16} />
                        </button>

                        <Link
                            to="/portal"
                            className="mt-6 block text-center text-xs text-[var(--text-secondary)] hover:text-[var(--accent-champagne)]"
                        >
                            ← Back to login
                        </Link>
                    </form>
                )}
            </motion.div>
        </div>
    );
}
